import { useMemo, useRef } from "react"

import { CamelToSnakeCaseNested, pythonify, unpythonify } from "@ncs/ts-utils"

import { useAuth } from "../../contexts"
import {
	ApiDeleteMutation,
	ApiGetQuery,
	ApiGetQueryOptions,
	ApiInfiniteGetQuery,
	ApiInfiniteGetQueryOptions,
	ApiPatchMutation,
	ApiPostMutation,
	useDeleteRequest,
	useGetRequest,
	useGetRequestEcommService,
	useInfiniteGetRequest,
	usePatchRequest,
	usePostRequest,
	usePaginationGetRequest,
	usePaginationGetRequestEcomm,
	ApiPaginationGetQuery,
} from "../../request-hooks"
import { apiClient, ecommApiClient, buildUrl, buildEcommUrl } from "../../util"
import { InventoryUrlPaths } from "../inventory"
import {
	BrandCard,
	BrandCardOrderPost,
	BrandCardPatch,
	BrandCardPost,
	BrandCardQueryParams,
	CarouselImage,
	CarouselImageOrderPost,
	CarouselImagePatch,
	CarouselImagePost,
	CarouselImageQueryParams,
	CreateCustomerIssuePost,
	CustomerManufacturer,
	CustomerPart,
	CustomerPartQueryParams,
	CustomerSystem,
	IssueReason,
	OnlinePartDetails,
	OnlinePartEcomm,
	OnlinePartsQueryParams,
	OnlinePartRelations,
	OnlinePartRelationsQueryParams,
	FrequentlyBoughtTogetherQueryParams,
	PartCategory,
	PartCategoryPatch,
	PartAssetImagesResponse,
	PartAssetPost,
	PartAssetPatch,
	PartCategoryPost,
	PointOfSaleUrlPaths,
	PosPart,
	PosPartPatch,
	PosPartPost,
	PosPartQueryParams,
	ProductCatalog,
	ProductCatalogDetails,
	ProductCatalogPartDelete,
	ProductCatalogPartPost,
	ProductCatalogPatch,
	ProductCatalogPost,
	ProductCatalogUserDelete,
	ProductCatalogUserPOst,
	ShippitResponse,
	EcommShippitResponse,
	UserBrandCardsPost,
	GetPublishedAssetsResponse,
} from "./types"

export const usePosPart = (
	id: string | null,
	options?: ApiGetQueryOptions<PosPart, PosPartQueryParams>
): ApiGetQuery<PosPart> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts, id ?? ""], {
		...options,
		queryConfig: {
			...options?.queryConfig,
			enabled: !!id,
		},
	})
}

export const defaultPosPartQueryParams: PosPartQueryParams = {
	search: null,
	ordering: null,
	category: null,
	part: null,
	active: null,
	featured: null,
	clearance: null,
	parent: null,
	child: null,
	partNumberSearch: null,
}

export const usePosParts = (
	options?: ApiInfiniteGetQueryOptions<PosPart, PosPartQueryParams>
): ApiInfiniteGetQuery<PosPart> => {
	return useInfiniteGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts], {
		...options,
		params: {
			...defaultPosPartQueryParams,
			...options?.params,
		},
	})
}

export const usePartsWithAssetsNotOnline = (
	options?: ApiInfiniteGetQueryOptions<any, any>
): ApiInfiniteGetQuery<any> => {
	return useInfiniteGetRequest([PointOfSaleUrlPaths.PointOfSale, "part_images_not_online"], {
		...options,
		params: {
			...defaultPosPartQueryParams,
			...options?.params,
		},
	})
}

export const usePartsAssetsWithoutInventoryPart = (
	options?: ApiInfiniteGetQueryOptions<any, any>
): ApiInfiniteGetQuery<any> => {
	return useInfiniteGetRequest(
		[PointOfSaleUrlPaths.PointOfSale, "part_assets_without_inventory_part"],
		{
			...options,
			params: {
				...defaultPosPartQueryParams,
				...options?.params,
			},
		}
	)
}

export const useCreatePosPart = (): ((partData: PosPartPost) => Promise<void>) => {
	const formDataPost = usePostRequest<FormData>(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts],
		{
			keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, InventoryUrlPaths.Inventory],
		}
	)

	// Convert the typed `PosPartPost` data into FormData.
	// Doing this here so the hook can preserve typing.
	const partDataPost = async (partData: PosPartPost): Promise<void> => {
		const form = new FormData()
		// Don't try to pythonify the image file (their key is "file").
		const pythonifiedPartData = pythonify(partData, "file")
		Object.entries(pythonifiedPartData).forEach(([key, value]) => {
			if (value) {
				form.append(key, value as string | Blob)
			}
		})

		await formDataPost(form)
	}

	return partDataPost
}

export const useUpdatePosPart = (): ApiPatchMutation<PosPartPatch> => {
	return usePatchRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, InventoryUrlPaths.Inventory],
	})
}

export const useUpdatePosPartImage = (): ApiPatchMutation<FormData> => {
	return usePatchRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, InventoryUrlPaths.Inventory],
	})
}

export const useDeletePosPart = (): ApiDeleteMutation => {
	return useDeleteRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, InventoryUrlPaths.Inventory],
	})
}

export const useCustomerPart = (
	onlinePartId?: string | null,
	options?: ApiGetQueryOptions<CustomerPart>
): ApiGetQuery<CustomerPart> => {
	const { isPrivilegedDbUser } = useAuth()

	return useGetRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CustomerParts, onlinePartId ?? ""],
		{
			...options,
			params: {
				...options?.params,
				pdbu: isPrivilegedDbUser ? true : undefined,
			},
			queryConfig: {
				enabled: !!onlinePartId,
				retry: 2,
				...options?.queryConfig,
			},
		}
	)
}

export const useOnlinePartEcommService = (
	onlinePartId?: string | null
): ApiGetQuery<CustomerPart> => {
	return useGetRequestEcommService(
		[PointOfSaleUrlPaths.Parts, PointOfSaleUrlPaths.OnlinePart, onlinePartId ?? ""],
		{
			queryConfig: {
				enabled: !!onlinePartId,
			},
		}
	)
}

export const useOnliePartsEcommService = (
	options?: ApiGetQueryOptions<OnlinePartEcomm[], OnlinePartsQueryParams>
): ApiGetQuery<OnlinePartEcomm[]> => {
	return useGetRequestEcommService(
		[PointOfSaleUrlPaths.Parts, PointOfSaleUrlPaths.OnlineParts],
		{
			...options,
			params: {
				...options?.params,
			},
		}
	)
}

export const useCustomerPartsEcomm = (
	options?: ApiInfiniteGetQueryOptions<CustomerPart, CustomerPartQueryParams> & {
		removeChildren?: boolean
	}
): ApiPaginationGetQuery<CustomerPart> => {
	const { isPrivilegedDbUser } = useAuth()

	const result = usePaginationGetRequest<
		CustomerPart,
		CustomerPartQueryParams & { pdbu?: true }
	>([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CustomerParts], {
		...options,
		params: {
			...options?.params,
			pdbu: isPrivilegedDbUser ? true : undefined,
		},
	})

	return {
		...result,
	}
}

export const useSearchOnlinePartsEcomm = (
	options?: ApiInfiniteGetQueryOptions<OnlinePartEcomm, CustomerPartQueryParams> & {
		removeChildren?: boolean
	}
): ApiPaginationGetQuery<OnlinePartEcomm> => {
	const { isPrivilegedDbUser } = useAuth()

	const result = usePaginationGetRequestEcomm<
		OnlinePartEcomm,
		CustomerPartQueryParams & { pdbu?: true }
	>([PointOfSaleUrlPaths.Parts, PointOfSaleUrlPaths.SearchOnlineParts], {
		...options,
		params: {
			...options?.params,
			pdbu: isPrivilegedDbUser ? true : undefined,
		},
	})

	return {
		...result,
	}
}

export const useSearchCustomerParts = (
	searchTerm: string | null,
	options?: ApiInfiniteGetQueryOptions<CustomerPart, CustomerPartQueryParams> & {
		removeChildren?: boolean
	}
): ApiPaginationGetQuery<CustomerPart> => {
	const { isPrivilegedDbUser } = useAuth()

	return usePaginationGetRequest<CustomerPart, CustomerPartQueryParams & { pdbu?: true }>(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CustomerParts, "search"],
		{
			...options,
			params: {
				...options?.params,
				search: searchTerm,
				pdbu: isPrivilegedDbUser ? true : undefined,
			},
			queryConfig: {
				...options?.queryConfig,
				enabled: !!searchTerm && options?.queryConfig?.enabled !== false,
			},
		}
	)
}

export const useOnlinePartRelations = (
	onlinePartId: string | null | undefined,
	options?: ApiGetQueryOptions<OnlinePartRelations, OnlinePartRelationsQueryParams>
): ApiGetQuery<OnlinePartRelations> => {
	return useGetRequest<OnlinePartRelations, OnlinePartRelationsQueryParams>(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.PartRelationship],
		{
			...options,
			queryConfig: {
				...options?.queryConfig,
				enabled: !!onlinePartId, // Call will error if `onlinePart` is not present.
			},
			params: {
				onlinePart: onlinePartId ?? "",
				...options?.params,
			},
		}
	)
}

export const useOnlinePartDetails = (
	onlinePartId?: string | null,
	options?: ApiGetQueryOptions<OnlinePartDetails>
): ApiGetQuery<OnlinePartDetails> => {
	return useGetRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.PartDetail, onlinePartId ?? ""],
		{
			...options,
			queryConfig: {
				...options?.queryConfig,
				enabled: !!onlinePartId,
			},
		}
	)
}

// Frequently bought together
// On the product detail page, a "Frequently Bought Together" section will appear under the add to cart button.
// Fetches recommendations based on the inventory part ID.
export const useFrequentlyBoughtTogether = (
	onlinePartId?: string | null,
	options?: ApiGetQueryOptions<CustomerPart[], FrequentlyBoughtTogetherQueryParams>
): ApiGetQuery<CustomerPart[]> => {
	return useGetRequest<CustomerPart[], FrequentlyBoughtTogetherQueryParams>(
		[
			PointOfSaleUrlPaths.PointOfSale,
			PointOfSaleUrlPaths.CustomerParts,
			PointOfSaleUrlPaths.FrequentlyBoughtTogether,
		],
		{
			...options,
			params: { online_part_id: onlinePartId ?? "" },
			queryConfig: {
				...options?.queryConfig,
				enabled: !!onlinePartId,
			},
		}
	)
}

// Frequently bought together (Ecomm Service)
// Uses the new ecomm-service endpoint for frequently bought together recommendations.
// This is the updated version that should be used going forward.
export const useFrequentlyBoughtTogetherEcomm = (
	onlinePartId?: string | null,
	options?: ApiGetQueryOptions<OnlinePartEcomm[], FrequentlyBoughtTogetherQueryParams>
): ApiGetQuery<OnlinePartEcomm[]> => {
	return useGetRequestEcommService<OnlinePartEcomm[], FrequentlyBoughtTogetherQueryParams>(
		[PointOfSaleUrlPaths.Parts, PointOfSaleUrlPaths.FrequentlyBoughtTogether],
		{
			...options,
			params: { online_part_id: onlinePartId ?? "" },
			queryConfig: {
				...options?.queryConfig,
				enabled: !!onlinePartId,
			},
		}
	)
}

export const useOnlinePartDetailsEcommService = (
	onlinePartId?: string | null
): ApiGetQuery<OnlinePartDetails> => {
	return useGetRequestEcommService(
		[PointOfSaleUrlPaths.Parts, PointOfSaleUrlPaths.OnlinePartDetail, onlinePartId ?? ""],
		{
			queryConfig: {
				enabled: !!onlinePartId,
			},
		}
	)
}

export const initialCustomerPartQueryParams: CustomerPartQueryParams = {
	search: null,
	manufacturers: [],
	systems: [],
	categories: [],
	price_Gte: null,
	price_Lt: null,
	featured: null,
	clearance: null,
	onlinePartId: [],
	chemicals: [],
	brands: [],
	ordering: null,
	show_children: null,
}

export const usePartManufacturers = (): ApiGetQuery<CustomerManufacturer[]> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Manufacturers], {
		queryConfig: {
			refetchOnWindowFocus: false,
		},
		mapper: (data) => {
			return unpythonify<CustomerManufacturer[]>([
				...data,
				// We're adding in Vacutech and TSS here manually. Remove this when it gets actually
				// added portal side, and update the hardcoded brand link on the Shope page.
				{
					id: 1000,
					code: "Vacutech",
					name: "Vacutech",
				},
				{
					id: 1001,
					code: "TSS",
					name: "TSS",
				},
			]).sort((a, b) => {
				// Since we're adding more items in, resort the array.
				if (a.name === b.name) return 0
				if (a.name === null) return 1
				if (b.name === null) return -1
				return a.name < b.name ? -1 : 1
			})
		},
	})
}

export const useCategories = (): ApiGetQuery<PartCategory[]> => {
	const [categories, isLoadingCategories, categoriesExtras] = usePartCategories()
	const [chemicals, isLoadingChemicals] = usePartChemicals()

	const allCategories = [...(categories ?? []), ...(chemicals ?? [])]
	allCategories.sort((a, b) => {
		const aName = a.name ?? ""
		const bName = b.name ?? ""
		return aName.localeCompare(bName)
	})

	return [allCategories, isLoadingCategories || isLoadingChemicals, categoriesExtras]
}

export const usePartCategories = (): ApiGetQuery<PartCategory[]> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories])
}

export const usePartVariantNames = (): ApiGetQuery<string[]> => {
	return useGetRequest([
		PointOfSaleUrlPaths.PointOfSale,
		PointOfSaleUrlPaths.Parts,
		PointOfSaleUrlPaths.VariantNames,
	])
}

export const usePartChemicals = (): ApiGetQuery<PartCategory[]> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories], {
		params: {
			isChemical: true,
		},
	})
}

export const useCreatePartCategory = (): ApiPostMutation<PartCategoryPost, PartCategory> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories],
	})
}

export const useUpdatePartCategory = (): ApiPatchMutation<PartCategoryPatch> => {
	return usePatchRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories],
	})
}

export const useDeletePartCategory = (): ApiDeleteMutation => {
	return useDeleteRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Categories],
	})
}

export const usePartSystems = (): ApiGetQuery<CustomerSystem[]> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Systems])
}

export const useEcommFreightEstimation = () => {
	const lastPayloadRef = useRef<string | null>(null)

	return async (
		cart: {
			part: CustomerPart
			quantity: number
		}[],
		destination: {
			shipToSiteId?: string
			alternateAddressId?: string | null
			alternateAddress?: {
				streetLines: string[]
				city: string
				state: string
				zipCode: string
			}
			billTo: {
				customer_number: string | null
				name: string | null
			}
		}
	): Promise<EcommShippitResponse | null> => {
		const { shipToSiteId, alternateAddress, alternateAddressId } = destination
		if (!shipToSiteId && !alternateAddress && !alternateAddressId) {
			throw new Error(
				"Ecomm Freight Estimation called without shipToSiteId, alternateAddressId or alternateAddress"
			)
		}

		const payload = {
			ship_to_id: shipToSiteId,
			alternate_address: pythonify(alternateAddress),
			alternate_address_id: alternateAddressId,
			parts: pythonify(
				cart.map(({ part, quantity }) => ({
					part_id: part?.part?.id,
					quantity,
				}))
			),
		}

		const payloadStr = JSON.stringify(payload)
		if (lastPayloadRef.current === payloadStr) {
			return null // Avoid duplicate request
		}
		lastPayloadRef.current = payloadStr

		const { data } = await ecommApiClient.post<CamelToSnakeCaseNested<EcommShippitResponse>>(
			buildEcommUrl("pricing/freight_estimation/"),
			payload
		)

		return unpythonify(data)
	}
}

export const useShippitApi = () => {
	const lastPayloadRef = useRef<string | null>(null)

	return async (
		cart: {
			part: CustomerPart
			quantity: number
		}[],
		destination: {
			shipToSiteId?: string
			alternateAddressId?: string | null
			alternateAddress?: {
				address1?: string | null
				address2: string
				city: string
				state: string
				postalcode: string
			}
			billTo: {
				customer_number: string | null
				name: string | null
			}
		}
	): Promise<ShippitResponse | null> => {
		const { shipToSiteId, alternateAddress, alternateAddressId } = destination
		if (!shipToSiteId && !alternateAddress && !alternateAddressId) {
			throw new Error(
				"shippitApi called without shipToSiteId, alternateAddressId or alternateAddress"
			)
		}

		const payload = {
			ship_to_id: shipToSiteId,
			alternate_address: pythonify(alternateAddress),
			alternate_address_id: alternateAddressId,
			items: pythonify(
				cart.map(({ part, quantity }) => ({
					id: part.id,
					quantity,
				}))
			),
		}

		const payloadStr = JSON.stringify(payload)
		if (lastPayloadRef.current === payloadStr) {
			return null // Avoid duplicate request
		}
		lastPayloadRef.current = payloadStr

		const { data } = await apiClient.post<CamelToSnakeCaseNested<ShippitResponse>>(
			buildUrl("point_of_sale/order/summary/"),
			payload
		)

		return unpythonify(data)
	}
}

export const useProductCatalogs = (
	options?: ApiGetQueryOptions<ProductCatalog[]>
): ApiGetQuery<ProductCatalog[]> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Catalog], options)
}

export const useProductCatalog = (
	catalogId: string | null | undefined
): [ProductCatalog | undefined, boolean] => {
	const [catalogs, isLoading] = useProductCatalogs()

	const catalog = useMemo(() => {
		return (catalogs ?? []).find((c) => c.id.toString() === catalogId)
	}, [catalogId, catalogs])

	return [catalog, isLoading]
}

export const useCreateProductCatalog = (): ApiPostMutation<
	ProductCatalogPost,
	{ catalogId: string }
> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Catalog], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useUpdateProductCatalog = (): ApiPatchMutation<ProductCatalogPatch> => {
	return usePatchRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Catalog], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useProductCatalogDetails = (
	id: string | null | undefined
): ApiGetQuery<ProductCatalogDetails> => {
	return useGetRequest(
		[
			PointOfSaleUrlPaths.PointOfSale,
			PointOfSaleUrlPaths.Details,
			PointOfSaleUrlPaths.Catalog,
			id ?? "",
		],
		{
			queryConfig: {
				enabled: !!id,
			},
		}
	)
}

export const useAddUserToCatalog = (): ApiPostMutation<ProductCatalogUserPOst> => {
	return usePostRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.User, PointOfSaleUrlPaths.Catalog],
		{
			keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
		}
	)
}

export const useRemoveUserFromCatalog = (): ApiDeleteMutation<ProductCatalogUserDelete> => {
	return useDeleteRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.User, PointOfSaleUrlPaths.Catalog],
		{
			keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
		}
	)
}

export const useAddPartToCatalog = (): ApiPostMutation<ProductCatalogPartPost> => {
	return usePostRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Part, PointOfSaleUrlPaths.Catalog],
		{
			keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
		}
	)
}

export const useRemovePartFromCatalog = (): ApiDeleteMutation<ProductCatalogPartDelete> => {
	return useDeleteRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Part, PointOfSaleUrlPaths.Catalog],
		{
			keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
		}
	)
}

export const useBrandCards = (
	options?: ApiGetQueryOptions<BrandCard[], BrandCardQueryParams>
): ApiGetQuery<BrandCard[]> => {
	return useGetRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.BrandCard], options)
}

export const useBrandCard = (id: string | null | undefined): ApiGetQuery<BrandCard> => {
	return useGetRequest<BrandCard, BrandCardQueryParams>(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.BrandCard],
		{
			params: {
				card: id,
			},
			queryConfig: {
				enabled: !!id,
			},
		}
	)
}

export const useCreateBrandCard = (): ApiPostMutation<BrandCardPost> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.BrandCard], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useUpdateBrandCard = (): ApiPatchMutation<BrandCardPatch> => {
	return usePatchRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.BrandCard], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useUploadBrandCardImage = (): ApiPostMutation<FormData, { imageUrl: string }> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.BrandCardImage], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useSetBrandCardOrder = (): ApiPostMutation<BrandCardOrderPost> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.BrandCardOrder], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useSetUserBrandCards = (): ApiPostMutation<UserBrandCardsPost> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.UserBrandCard], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useCarouselImages = (
	options?: ApiGetQueryOptions<CarouselImage[], CarouselImageQueryParams>
): ApiGetQuery<CarouselImage[]> => {
	return useGetRequest(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CarouselImage],
		options
	)
}

export const useCarouselImage = (id?: string): ApiGetQuery<CarouselImage> => {
	return useGetRequest<CarouselImage, CarouselImageQueryParams>(
		[PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CarouselImage, id ?? ""],
		{
			queryConfig: {
				enabled: !!id,
			},
		}
	)
}

export const useCreateCarouselImage = (): ApiPostMutation<CarouselImagePost> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CarouselImage], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useUpdateCarouselImage = (): ApiPatchMutation<CarouselImagePatch> => {
	return usePatchRequest([PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.CarouselImage], {
		keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
	})
}

export const useUploadCarouselImage = (): ApiPostMutation<FormData, { imageUrl: string }> => {
	return usePostRequest(
		[
			PointOfSaleUrlPaths.PointOfSale,
			PointOfSaleUrlPaths.CarouselImage,
			PointOfSaleUrlPaths.CarouselImageUpload,
		],
		{
			keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
		}
	)
}

export const useSetCarouselImageOrder = (): ApiPostMutation<CarouselImageOrderPost> => {
	return usePostRequest(
		[
			PointOfSaleUrlPaths.PointOfSale,
			PointOfSaleUrlPaths.CarouselImage,
			PointOfSaleUrlPaths.CarouselImageOrder,
		],
		{
			keyToInvalidate: PointOfSaleUrlPaths.PointOfSale,
		}
	)
}

export const useCustomerIssueReasons = (): ApiGetQuery<IssueReason[]> => {
	return useGetRequest([
		PointOfSaleUrlPaths.PointOfSale,
		PointOfSaleUrlPaths.Issue,
		PointOfSaleUrlPaths.Reason,
	])
}

export const useCreateCustomerIssue = (): ApiPostMutation<CreateCustomerIssuePost> => {
	return usePostRequest([
		PointOfSaleUrlPaths.PointOfSale,
		PointOfSaleUrlPaths.Issue,
		PointOfSaleUrlPaths.Submission,
	])
}

export const usePublishedAssets = (
	partId: string | null | undefined,
	options?: ApiGetQueryOptions<GetPublishedAssetsResponse>
): ApiGetQuery<GetPublishedAssetsResponse> => {
	return useGetRequest(
		[
			PointOfSaleUrlPaths.PointOfSale,
			PointOfSaleUrlPaths.Parts,
			partId ?? "",
			PointOfSaleUrlPaths.PublishedAssets,
		],
		options
	)
}

export const usePartAssets = (
	partId: string | null | undefined
): ApiGetQuery<PartAssetImagesResponse> => {
	return useGetRequest(
		[
			PointOfSaleUrlPaths.PointOfSale,
			PointOfSaleUrlPaths.Parts,
			partId ?? "",
			PointOfSaleUrlPaths.PartAssets,
		],
		{
			queryConfig: {
				enabled: !!partId,
			},
		}
	)
}

export const useCreatePartAsset = () => {
	const create = usePostRequest<FormData>([PointOfSaleUrlPaths.PointOfSale, "part_assets"], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts],
	})

	const handleCreate = async (payload: PartAssetPost): Promise<void> => {
		const data = new FormData()
		if (payload.file) {
			data.append("file", payload.file as Blob, payload.file.name)
		}

		if (payload.description) {
			data.append("description", payload.description)
		}

		if ((payload as any).published !== undefined) {
			data.append("published", String((payload as any).published))
		}

		if ((payload as any).partId !== undefined) {
			data.append("part_id", String((payload as any).partId))
		}

		await create(data)
	}

	return handleCreate
}

export const useCreateOnlinePartVariant = () => {
	const create = usePostRequest<FormData>(
		[PointOfSaleUrlPaths.PointOfSale, "online_part_variants"],
		{
			keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, "online_part_variants"],
		}
	)

	const handleCreate = async (payload: PartAssetPost): Promise<void> => {
		const data = new FormData()
		if (payload.file) {
			data.append("file", payload.file as Blob, payload.file.name)
		}

		if (payload.description) {
			data.append("description", payload.description)
		}

		if ((payload as any).published !== undefined) {
			data.append("published", String((payload as any).published))
		}

		if ((payload as any).partId !== undefined) {
			data.append("part_id", String((payload as any).partId))
		}

		await create(data)
	}

	return handleCreate
}

export const useUpdatePartAsset = (partId: string | null | undefined) => {
	const update = usePatchRequest<FormData>([PointOfSaleUrlPaths.PointOfSale, "part_assets"], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts],
	})

	const handleUpdate = async (payload: PartAssetPatch): Promise<void> => {
		const data = new FormData()
		if (payload.file !== null && payload.file !== undefined) {
			data.append("file", payload.file as Blob, payload.file.name)
		}

		if (payload.description) {
			data.append("description", payload.description)
		}

		if (payload.isCover) {
			data.append("is_cover", String(payload.isCover))
		}

		if ((payload as any).published !== undefined) {
			data.append("published", String((payload as any).published))
		}

		await update({ updates: data, id: partId as any })
	}

	return handleUpdate
}

export const useDeletePartAsset = (partId: string | null | undefined): ApiDeleteMutation => {
	return useDeleteRequest([PointOfSaleUrlPaths.PointOfSale, "part_assets", partId ?? ""], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, InventoryUrlPaths.Parts],
	})
}

export const useReorderPartAssets = (partId: string | null | undefined): ApiPostMutation<any> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, "part_assets", "reorder_assets"], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts],
	})
}

export const usePublishAllPartAssets = (
	partId: string | null | undefined
): ApiPostMutation<any> => {
	return usePostRequest([PointOfSaleUrlPaths.PointOfSale, "part_assets", "publish_all"], {
		keyToInvalidate: [PointOfSaleUrlPaths.PointOfSale, PointOfSaleUrlPaths.Parts],
	})
}
